import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Spacer } from "../../components/Layout/Layout";
import TextInput from "../../ui-lib/components/Inputs/TextInput";
import useUser from "../../core/user/useUser";
import { createReseller } from "../../core/api/sellers/sellers";
import { INewReseller } from "../../core/api/sellers/types";
import { notifyApiErrors } from "../../core/helpers/helpers";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";

const AddResellerModal = ({
  isAddSellerModalOpen,
  setIsAddSellerModalOpen,
  submitHandler,
}: {
  isAddSellerModalOpen: boolean;
  setIsAddSellerModalOpen: (value: React.SetStateAction<boolean>) => void;
  submitHandler: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const { authenticatedRequest } = useUser();

  const [loading, setLoading] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    clearErrors,
    reset,
  } = useForm<INewReseller>();

  const onClose = () => {
    clearErrors();
    reset();
    setIsAddSellerModalOpen(false);
  };

  const onSubmit = async (values: INewReseller) => {
    setLoading(true);
    try {
      const result = await createReseller(authenticatedRequest, values);
      notify({ message: "Administrator:reseller_created_successfully" });
      submitHandler(result.data);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PrimeModal
      withFooter
      withHeader
      loading={loading}
      header={t("Common:add_seller")}
      fixedWidth="576px"
      isOpen={isAddSellerModalOpen}
      onClose={onClose}
      submitBtn={{
        text: t("Common:create_seller"),
        onClick: handleSubmit(onSubmit),
      }}
      cancelBtn={{ text: t("Common:cancel"), onClick: onClose }}
    >
      <p>{t("Administrator:add_reseller_description")}</p>
      <Spacer size={16} />
      <form>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              required
              label={t("Common:name")}
              placeholder="Enter reseller name"
              value={field.value}
              onChange={field.onChange}
              validationError={errors?.name && t("Errors:input_field_required")}
            />
          )}
        />
      </form>
    </PrimeModal>
  );
};

export default AddResellerModal;
